import { memo, useEffect, useMemo, useState } from "react";

import { Button } from "primereact/button";
import { Chart } from "primereact/chart";

import { Chart as ChartJS } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import annotationPlugin from "chartjs-plugin-annotation";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

ChartJS.register(annotationPlugin);

export const GraficoProducaoResiduo = memo(function GraficoProducaoResiduo({
  months,
  producao_geral,
  residuos,
  setor,
  residuoAproximado = false,
  graficoComMeta = false,
  setores = [],
}) {
  const [useKg, setUseKg] = useState(false);
  const [tecModal, setTecModal] = useState(false);

  const meta = useMemo(() => {
    if (!graficoComMeta) return 0;
    const setorEncontrado = setores.find((s) => s.nome === setor);
    return setorEncontrado?.meta_producao ?? 0;
  }, [graficoComMeta, setores, setor]);

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          anchor: "end",
          align: "end",
          formatter: (value) =>
            Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(
              value
            ),
          font: { size: 14, weight: "bold" },
          color: "#2c3e50",
        },
        annotation: graficoComMeta
          ? {
              annotations: {
                line1: {
                  type: "line",
                  yMin: meta,
                  yMax: meta,
                  borderColor: "rgb(255, 99, 132)",
                  borderWidth: 2,
                },
              },
            }
          : {},
      },
    }),
    [meta, graficoComMeta]
  );

  const chartData = useMemo(() => {
    const filteredData = (producao_geral ?? []).filter(
      (element) => element.setor === setor
    );

    const data = [];
    const tecMap = new Map();

    for (let i = 1; i <= 12; i++) {
      const prod = filteredData.filter(
        (element) => parseInt(element.mes) === i
      );

      let total = 0;

      for (const item of prod) {
        if (residuoAproximado) {
          if (item.pesoresiduos !== null && item.pesoresiduos !== undefined) {
            total += parseFloat(item.pesoresiduos);
          }
        } else {
          const base = useKg ? item.somaemkg : item.soma;
          if (base !== null && base !== undefined) {
            total += parseFloat(base);
          }
        }

        if (item.tec_grama && item.tec_grama.includes(":")) {
          const [descricao, valor] = item.tec_grama.split(":");
          const desc = descricao.trim();
          const num = parseFloat(valor.trim());

          if (desc && !isNaN(num)) {
            tecMap.set(desc, num);
          }
        }
      }
      data.push(prod.length ? total : 0);
    }

    const tec = Array.from(tecMap, ([descricao, gramaMetro]) => ({
      descricao,
      gramaMetro,
    }));

    return {
      labels: months,
      datasets: [
        {
          backgroundColor: residuoAproximado ? "#feca57" : "#48dbfb",
          data,
          label: residuoAproximado
            ? "GERAÇÃO APROXIMADA DE RESÍDUOS POR MÊS"
            : "PRODUÇÃO GERAL",
        },
      ],
      tec
    };
  }, [months, producao_geral, setor, residuoAproximado, useKg]);

  useEffect(() => {
    setUseKg(false);
  }, [setor]);

  return (
    <div>
      {!residuoAproximado && (
        <Button
          label={useKg ? "KG" : "METROS"}
          onClick={() => setUseKg((v) => !v)}
          style={{ width: "10%" }}
        />
      )}
      {chartData.tec.length ? (
        <Button
          label={"Ver Grama/Metro"}
          onClick={() => setTecModal(true)}
          style={{ width: "10%", marginLeft: "10px"}}
        />
      ) : null}

      <Chart
        data={chartData}
        height="350px"
        options={chartOptions}
        plugins={[ChartDataLabels]}
        type="bar"
      />

      <Dialog
        header="Tecidos por Grama/Metro"
        visible={tecModal}
        onHide={() => setTecModal(false)}
        style={{ width: "60%" }}
        modal
      >
        <DataTable value={chartData.tec}>
          <Column field="descricao" header="Descrição" />
          <Column field="gramaMetro" header="Grama/Metro" body={(row) => row.gramaMetro.toFixed(4)} />
        </DataTable>
      </Dialog>
    </div>
  );
});
